import { getCoverThumbUrl } from '../../utils/coverUrl';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { User, Camera, X, Play, GripVertical, Pin, PinOff, Tag, Plus, Newspaper, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { buildMediaItems } from './MediaCarousels';
import PressReleaseAdminEditor from './PressReleaseAdminEditor';
import ManuscriptReplacer from './ManuscriptReplacer';

const getCoverUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${window.location.origin}${url}`;
  return url;
};

const TYPE_LABEL = {
  youtube: { name: 'YouTube', cls: 'bg-red-500/20 text-red-400' },
  soundcloud: { name: 'SoundCloud', cls: 'bg-orange-500/20 text-orange-400' },
  spotify: { name: 'Spotify', cls: 'bg-green-500/20 text-green-400' },
};

/**
 * Builds the unified ordered list of media items (across types) for the
 * "Display Order" drag-and-drop section. Each item is identified by its url.
 */
const buildUnifiedList = (book, currentOrder) => {
  const { youtube, soundcloud, spotify } = buildMediaItems({
    ...book,
    media_order: currentOrder, // honor in-modal pending order
  });
  const tagged = [
    ...youtube.map((it) => ({ ...it, type: 'youtube' })),
    ...soundcloud.map((it) => ({ ...it, type: 'soundcloud' })),
    ...spotify.map((it) => ({ ...it, type: 'spotify' })),
  ];
  // Sort across all types by current order (items not in order go last, type-grouped)
  const orderIdx = (url) => {
    const i = currentOrder?.indexOf(url) ?? -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return tagged
    .map((it, naturalIdx) => ({ it, naturalIdx, oi: orderIdx(it.url) }))
    .sort((a, b) => a.oi - b.oi || a.naturalIdx - b.naturalIdx)
    .map((x) => x.it);
};

const MetaEditorModal = ({
  open,
  onClose,
  book,
  userRole,
  uploadingPhoto,
  onAuthorPhotoUpload,
  // controlled edit state (kept in parent for backward compat)
  editAuthorBio, setEditAuthorBio,
  editSeriesName, setEditSeriesName,
  editSeriesOrder, setEditSeriesOrder,
  editYoutubeUrl, setEditYoutubeUrl,
  editYoutubeUrl2, setEditYoutubeUrl2,
  editSoundcloudUrl, setEditSoundcloudUrl,
  editSpotifyUrl, setEditSpotifyUrl,
  editTrailerExternalUrl, setEditTrailerExternalUrl,
  editMediaLinks, setEditMediaLinks,
  editMediaOrder, setEditMediaOrder,
  editMediaPinned, setEditMediaPinned,
  editDisabledEmbeds, setEditDisabledEmbeds,
  editGenres = [], setEditGenres,
  editTitle = '', setEditTitle,
  editAuthor = '', setEditAuthor,
  renameAllBooks = false, setRenameAllBooks,
  editDescription = '', setEditDescription,
  editPrice = '', setEditPrice,
  bookId,
  token,
  saving,
  onSave,
  onCoverUpdated,
}) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [genreInput, setGenreInput] = useState('');
  const [editingGenreIdx, setEditingGenreIdx] = useState(null);
  const [editingGenreValue, setEditingGenreValue] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverSrc, setCoverSrc] = useState(book?.cover_image_url || null);
  const [coverMsg, setCoverMsg] = useState(null);

  useEffect(() => {
    setCoverSrc(book?.cover_image_url || null);
  }, [book?.cover_image_url]);

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setCoverMsg('File must be an image (JPG, PNG or WebP).'); return; }
    if (file.size > 10 * 1024 * 1024) { setCoverMsg('Cover image must be under 10MB.'); return; }
    setCoverUploading(true);
    setCoverMsg(null);
    try {
      const fd = new FormData();
      fd.append('cover', file);
      const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await axios.post(`${API}/api/books/${bookId}/cover`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCoverSrc(res.data.cover_image_url);
      setCoverMsg('Cover updated! It now shows everywhere on the site.');
      onCoverUpdated?.();
    } catch (e) {
      setCoverMsg(e.response?.data?.detail || 'Upload failed — please try again.');
    } finally {
      setCoverUploading(false);
    }
  };

  // Build a "pending book" object that reflects the current modal edits so the
  // unified list updates as admin types/clears URLs.
  const pendingBook = useMemo(() => ({
    ...book,
    youtube_url: editYoutubeUrl || null,
    youtube_url_2: editYoutubeUrl2 || null,
    soundcloud_url: editSoundcloudUrl || null,
    spotify_url: editSpotifyUrl || null,
    media_links: editMediaLinks,
    media_pinned: editMediaPinned || [],
  }), [book, editYoutubeUrl, editYoutubeUrl2, editSoundcloudUrl, editSpotifyUrl, editMediaLinks, editMediaPinned]);

  const unifiedItems = useMemo(
    () => buildUnifiedList(pendingBook, editMediaOrder || []),
    [pendingBook, editMediaOrder]
  );

  if (!open || userRole !== 'admin') return null;

  const addGenre = () => {
    const v = (genreInput || '').trim();
    if (!v || !setEditGenres) return;
    const exists = (editGenres || []).some((g) => g.toLowerCase() === v.toLowerCase());
    if (exists) {
      setGenreInput('');
      return;
    }
    setEditGenres([...(editGenres || []), v]);
    setGenreInput('');
  };

  const removeGenre = (idx) => {
    if (!setEditGenres) return;
    setEditGenres((editGenres || []).filter((_, i) => i !== idx));
  };

  const startEditGenre = (idx) => {
    setEditingGenreIdx(idx);
    setEditingGenreValue((editGenres || [])[idx] || '');
  };

  const commitEditGenre = () => {
    if (editingGenreIdx === null || !setEditGenres) {
      setEditingGenreIdx(null);
      return;
    }
    const v = (editingGenreValue || '').trim();
    if (!v) {
      // empty -> remove
      setEditGenres((editGenres || []).filter((_, i) => i !== editingGenreIdx));
    } else {
      const next = [...(editGenres || [])];
      next[editingGenreIdx] = v;
      setEditGenres(next);
    }
    setEditingGenreIdx(null);
    setEditingGenreValue('');
  };

  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // some browsers need data set to start drag
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignore */ }
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIdx === null || dragIdx === idx) return;
    // Live reorder for visual feedback
    const next = [...unifiedItems];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setEditMediaOrder(next.map((it) => it.url));
    setDragIdx(idx);
  };

  const handleDragEnd = () => setDragIdx(null);

  const moveBy = (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= unifiedItems.length) return;
    const next = [...unifiedItems];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    setEditMediaOrder(next.map((it) => it.url));
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <User className="h-6 w-6 text-teal-500 mr-2" />
          Edit Book Details
        </h3>

        {/* Tabs (FEAT-008 — Press Releases) */}
        <div className="flex items-center gap-1 mb-4 bg-black/30 p-1 rounded-lg w-fit" data-testid="meta-editor-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            data-testid="meta-tab-details"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'details'
                ? 'bg-teal-500 text-white shadow-sm'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            Book details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('press')}
            data-testid="meta-tab-press"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
              activeTab === 'press'
                ? 'bg-fuchsia-500 text-white shadow-sm'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <Newspaper className="h-3 w-3" /> Press Releases
          </button>
        </div>

        {activeTab === 'press' ? (
          <div className="space-y-4">
            {bookId && token ? (
              <PressReleaseAdminEditor bookId={bookId} token={token} />
            ) : (
              <p className="text-sm text-gray-500 italic">Save the book first to manage press releases.</p>
            )}
            <div className="flex justify-end pt-3">
              <Button variant="ghost" onClick={onClose} className="text-gray-400">Close</Button>
            </div>
          </div>
        ) : (
        <div className="space-y-4">
          {/* Title */}
          <div>
            <Label className="text-white mb-2 block">Title</Label>
            <Input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle && setEditTitle(e.target.value)}
              placeholder="Book title"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              data-testid="edit-title"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-white mb-2 block">Description</Label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription && setEditDescription(e.target.value)}
              rows={3}
              placeholder="What is this book about?"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-gray-500 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="edit-description"
            />
          </div>

          {/* Price */}
          <div>
            <Label className="text-white mb-2 block">Price (USD)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={editPrice}
              onChange={(e) => setEditPrice && setEditPrice(e.target.value)}
              placeholder="0.00"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 w-32"
              data-testid="edit-price"
            />
          </div>

          {/* Genres */}
          <div>
            <Label className="text-white mb-2 block flex items-center gap-2">
              <Tag className="h-4 w-4 text-fuchsia-400" />
              Genres
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                value={genreInput}
                onChange={(e) => setGenreInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addGenre();
                  }
                }}
                placeholder="Add a genre and press Enter (e.g. Fantasy)"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 flex-1"
                data-testid="edit-genre-input"
              />
              <Button
                type="button"
                onClick={addGenre}
                disabled={!genreInput.trim()}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 shrink-0"
                data-testid="add-genre-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="genre-chip-list">
              {(editGenres || []).length === 0 && (
                <p className="text-xs text-gray-500 italic">No genres yet — add at least one to help readers discover this book.</p>
              )}
              {(editGenres || []).map((g, idx) => {
                const isEditing = editingGenreIdx === idx;
                return (
                  <span
                    key={`${g}-${idx}`}
                    className="inline-flex items-center gap-1 bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-200 text-xs px-2 py-1 rounded-full"
                    data-testid={`genre-chip-${idx}`}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingGenreValue}
                        autoFocus
                        onChange={(e) => setEditingGenreValue(e.target.value)}
                        onBlur={commitEditGenre}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEditGenre();
                          if (e.key === 'Escape') {
                            setEditingGenreIdx(null);
                            setEditingGenreValue('');
                          }
                        }}
                        className="bg-transparent border-b border-fuchsia-400 outline-none w-24 text-fuchsia-100"
                        data-testid={`genre-chip-input-${idx}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditGenre(idx)}
                        className="hover:underline"
                        title="Click to rename"
                        data-testid={`genre-chip-edit-${idx}`}
                      >
                        {g}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeGenre(idx)}
                      className="text-fuchsia-300 hover:text-red-300"
                      title="Remove genre"
                      data-testid={`genre-chip-remove-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="border-t border-white/10 pt-4 mt-4">
          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Cover Design</h4>
          </div>

          <div className="flex items-start gap-4">
            {coverSrc ? (
              <img
                src={getCoverThumbUrl(coverSrc)}
                alt="Current cover"
                className="w-20 h-[120px] rounded-lg object-cover border border-white/15 flex-shrink-0"
                data-testid="cover-current-preview"
              />
            ) : (
              <div className="w-20 h-[120px] rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <ImageIcon className="h-6 w-6 text-gray-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <input
                type="file"
                accept="image/*"
                id="replace-cover-input"
                className="hidden"
                data-testid="replace-cover-input"
                onChange={(e) => { uploadCover(e.target.files?.[0]); e.target.value = ''; }}
              />
              <label
                htmlFor="replace-cover-input"
                className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 cursor-pointer transition-colors"
                data-testid="replace-cover-btn"
              >
                {coverUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                {coverSrc ? 'Replace Cover Design' : 'Add Cover Design'}
              </label>
              <p className="text-xs text-gray-500 mt-2">
                JPG, PNG or WebP, up to 10MB. The new cover replaces the old one everywhere immediately.
              </p>
              {coverMsg && (
                <p
                  className={`text-xs mt-1.5 ${coverMsg.startsWith('Cover updated') ? 'text-emerald-400' : 'text-red-400'}`}
                  data-testid="cover-upload-msg"
                >
                  {coverMsg}
                </p>
              )}
            </div>
          </div>

          <ManuscriptReplacer book={book} bookId={bookId} token={token} onReplaced={onCoverUpdated} />

          <div className="border-t border-white/10 pt-4 mt-4">
          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Author & Series</h4>
          </div>

          <div>
            <Label className="text-white mb-2 block">Author's Name</Label>
            <Input
              type="text"
              value={editAuthor}
              onChange={(e) => setEditAuthor && setEditAuthor(e.target.value)}
              placeholder="Author's full name"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              data-testid="edit-author-name"
            />
            <p className="text-xs text-gray-500 mt-1">Renaming updates this book's public author page grouping.</p>
            {editAuthor.trim() && book?.author && editAuthor.trim() !== book.author && (
              <label
                className="flex items-center gap-2 mt-2 text-sm text-teal-200 bg-teal-500/10 border border-teal-500/25 rounded-lg px-3 py-2 cursor-pointer"
                data-testid="rename-all-books-row"
              >
                <input
                  type="checkbox"
                  checked={renameAllBooks}
                  onChange={(e) => setRenameAllBooks && setRenameAllBooks(e.target.checked)}
                  className="accent-teal-500 h-4 w-4"
                  data-testid="rename-all-books-checkbox"
                />
                Also rename "{book.author}" on all their other books
              </label>
            )}
          </div>

          <div>
            <Label className="text-white mb-2 block">Author's Biography</Label>
            <textarea
              placeholder="Write about the author..."
              value={editAuthorBio}
              onChange={(e) => setEditAuthorBio(e.target.value)}
              rows={4}
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-gray-500 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="edit-author-bio"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Author Photo</Label>
            <div className="flex items-center gap-3">
              {book?.author_photo_url ? (
                <img src={getCoverThumbUrl(book.author_photo_url)} alt="Author" className="w-12 h-12 rounded-full object-cover border border-white/20" />
              ) : (
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <label className="cursor-pointer bg-teal-600/30 hover:bg-teal-600/50 border border-teal-500/30 text-teal-300 text-sm px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2">
                <Camera className="h-4 w-4" />
                {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                <input type="file" accept="image/*" onChange={onAuthorPhotoUpload} className="hidden" data-testid="meta-author-photo-input" />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Series Name</Label>
              <Input
                type="text"
                placeholder='e.g., "Harry Potter"'
                value={editSeriesName}
                onChange={(e) => setEditSeriesName(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                data-testid="edit-series-name"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Book # in Series</Label>
              <Input
                type="number"
                min="1"
                placeholder="e.g., 1"
                value={editSeriesOrder}
                onChange={(e) => setEditSeriesOrder(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                data-testid="edit-series-order"
              />
            </div>
          </div>

          {/* Fixed media URLs with X-clear */}
          <FixedMediaInput label="YouTube Link 1" testId="edit-youtube-url" clearTestId="clear-youtube-url" iconType="youtube" value={editYoutubeUrl} setValue={setEditYoutubeUrl} placeholder="https://www.youtube.com/watch?v=..." />
          <FixedMediaInput label="YouTube Link 2 (Optional)" testId="edit-youtube-url-2" clearTestId="clear-youtube-url-2" iconType="youtube" value={editYoutubeUrl2} setValue={setEditYoutubeUrl2} placeholder="https://www.youtube.com/watch?v=..." />
          <FixedMediaInput label="SoundCloud Link" testId="edit-soundcloud-url" clearTestId="clear-soundcloud-url" iconType="soundcloud" value={editSoundcloudUrl} setValue={setEditSoundcloudUrl} placeholder="https://soundcloud.com/..." />
          <FixedMediaInput label="Spotify Link (Optional)" testId="edit-spotify-url" clearTestId="clear-spotify-url" iconType="spotify" value={editSpotifyUrl} setValue={setEditSpotifyUrl} placeholder="https://open.spotify.com/episode/..." />

          {/* Trailer External URL */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <Label className="text-white mb-2 block flex items-center gap-2">
              <Play className="h-4 w-4 text-red-500" />
              Book Trailer Link (YouTube or Facebook — Optional)
            </Label>
            <Input
              type="url"
              placeholder="https://www.youtube.com/watch?v=... or Facebook video link"
              value={editTrailerExternalUrl}
              onChange={(e) => setEditTrailerExternalUrl(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              data-testid="edit-trailer-external-url"
            />
            <p className="text-xs text-gray-500 mt-1">Used instead of an uploaded trailer file. Loads instantly with no buffering.</p>
          </div>

          {/* Dynamic Media Links */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-white font-semibold">Additional Media Links</Label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setEditMediaLinks([...editMediaLinks, { _uid: (crypto.randomUUID?.() || `mli-${Date.now()}-${Math.random()}`), type: 'youtube', url: '', label: '' }])} className="px-2 py-1 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded transition-colors" data-testid="add-youtube-link">+ YouTube</button>
                <button type="button" onClick={() => setEditMediaLinks([...editMediaLinks, { _uid: (crypto.randomUUID?.() || `mli-${Date.now()}-${Math.random()}`), type: 'soundcloud', url: '', label: '' }])} className="px-2 py-1 text-xs bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded transition-colors" data-testid="add-soundcloud-link">+ SoundCloud</button>
                <button type="button" onClick={() => setEditMediaLinks([...editMediaLinks, { _uid: (crypto.randomUUID?.() || `mli-${Date.now()}-${Math.random()}`), type: 'spotify', url: '', label: '' }])} className="px-2 py-1 text-xs bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded transition-colors" data-testid="add-spotify-link">+ Spotify</button>
              </div>
            </div>
            {editMediaLinks.map((link, idx) => (
              <div key={link._uid || `${link.type}-${link.url}-${idx}`} className="flex gap-2 mb-2 items-start bg-white/5 p-3 rounded-lg border border-white/5">
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded shrink-0 ${TYPE_LABEL[link.type]?.cls}`}>{link.type}</span>
                    <Input
                      type="text"
                      placeholder="Label (e.g., Radio Interview Part 2)"
                      value={link.label}
                      onChange={(e) => {
                        const updated = [...editMediaLinks];
                        updated[idx] = { ...updated[idx], label: e.target.value };
                        setEditMediaLinks(updated);
                      }}
                      className="bg-white/5 border-white/10 text-white text-xs h-7 placeholder:text-gray-600"
                    />
                  </div>
                  <Input
                    type="url"
                    placeholder={link.type === 'youtube' ? 'https://www.youtube.com/watch?v=...' : link.type === 'soundcloud' ? 'https://soundcloud.com/... or on.soundcloud.com/...' : 'https://open.spotify.com/episode/...'}
                    value={link.url}
                    onChange={(e) => {
                      const updated = [...editMediaLinks];
                      updated[idx] = { ...updated[idx], url: e.target.value };
                      setEditMediaLinks(updated);
                    }}
                    className="bg-white/5 border-white/10 text-white text-xs h-7 placeholder:text-gray-600"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setEditMediaLinks(editMediaLinks.filter((_, i) => i !== idx))}
                  className="text-red-400 hover:text-red-300 p-1 shrink-0 mt-1"
                  data-testid={`remove-media-link-${idx}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {editMediaLinks.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-2">No additional links. Click the buttons above to add.</p>
            )}
          </div>

          {/* Display Order — Drag & Drop reorder */}
          {unifiedItems.length > 1 && (            <div className="border-t border-white/10 pt-4 mt-4" data-testid="media-order-section">
              <Label className="text-white font-semibold mb-1 block">Display Order</Label>
              <p className="text-xs text-gray-500 mb-3">
                Drag to reorder. Click the pin to lock an item to the first slot of its carousel — pinned items always show first, even if you add more media later.
              </p>
              <div className="space-y-2">
                {unifiedItems.map((item, idx) => {
                  const isPinned = (editMediaPinned || []).includes(item.url);
                  return (
                    <div
                      key={item.url}
                      draggable
                      onDragStart={handleDragStart(idx)}
                      onDragOver={handleDragOver(idx)}
                      onDragEnd={handleDragEnd}
                      onDrop={handleDragEnd}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-grab active:cursor-grabbing ${
                        isPinned
                          ? 'bg-amber-500/10 border-amber-500/40'
                          : 'bg-white/5 border-white/5'
                      } ${dragIdx === idx ? 'opacity-50' : ''}`}
                      data-testid={`media-order-item-${idx}`}
                    >
                      <GripVertical className="h-4 w-4 text-gray-500 shrink-0" />
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${TYPE_LABEL[item.type]?.cls}`}>{TYPE_LABEL[item.type]?.name}</span>
                      {isPinned && (
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 bg-amber-500/30 text-amber-300 flex items-center gap-1"
                          data-testid={`media-pinned-badge-${idx}`}
                        >
                          <Pin className="h-2.5 w-2.5" /> PINNED
                        </span>
                      )}
                      <span className="text-xs text-gray-300 truncate flex-1" title={item.label}>{item.label}</span>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            const cur = editMediaPinned || [];
                            if (isPinned) {
                              setEditMediaPinned(cur.filter((u) => u !== item.url));
                            } else {
                              setEditMediaPinned([...cur, item.url]);
                            }
                          }}
                          className={`px-1 ${isPinned ? 'text-amber-400 hover:text-amber-300' : 'text-gray-400 hover:text-white'}`}
                          title={isPinned ? 'Unpin' : 'Pin to top'}
                          data-testid={`media-pin-toggle-${idx}`}
                        >
                          {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                        </button>
                        <button type="button" onClick={() => moveBy(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-white disabled:opacity-30 px-1 text-xs" data-testid={`media-order-up-${idx}`}>▲</button>
                        <button type="button" onClick={() => moveBy(idx, 1)} disabled={idx === unifiedItems.length - 1} className="text-gray-400 hover:text-white disabled:opacity-30 px-1 text-xs" data-testid={`media-order-down-${idx}`}>▼</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-white/10 pt-4 mt-4" data-testid="force-fallback-section">
            <Label className="text-white font-semibold mb-1 block">Force "Open in {'{Service}'}" Mode</Label>
            <p className="text-xs text-gray-500 mb-3">
              For problematic embeds: hide the iframe entirely on this book and show only the fallback link. Useful when an upstream player is unreliable for a particular video/episode.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { id: 'youtube', label: 'YouTube', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
                { id: 'soundcloud', label: 'SoundCloud', cls: 'bg-orange-500/20 text-orange-300 border-orange-500/40' },
                { id: 'spotify', label: 'Spotify', cls: 'bg-green-500/20 text-green-300 border-green-500/40' },
              ].map((svc) => {
                const checked = (editDisabledEmbeds || []).includes(svc.id);
                return (
                  <label
                    key={svc.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${checked ? svc.cls : 'bg-white/5 text-gray-400 border-white/10'}`}
                    data-testid={`force-fallback-${svc.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const cur = editDisabledEmbeds || [];
                        if (e.target.checked) {
                          setEditDisabledEmbeds([...cur, svc.id]);
                        } else {
                          setEditDisabledEmbeds(cur.filter((s) => s !== svc.id));
                        }
                      }}
                      className="accent-amber-500"
                    />
                    {svc.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-meta-btn">
              {saving ? 'Saving...' : 'Save Info'}
            </Button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

const TypeIcon = ({ iconType }) => {
  if (iconType === 'youtube') {
    return (
      <svg className="h-4 w-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
        <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#fff" />
      </svg>
    );
  }
  if (iconType === 'soundcloud') {
    return (
      <svg className="h-4 w-4 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.057-.05-.1-.1-.1" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02z" />
    </svg>
  );
};

const FixedMediaInput = ({ label, testId, clearTestId, iconType, value, setValue, placeholder }) => (
  <div>
    <Label className="text-white mb-2 block flex items-center gap-2">
      <TypeIcon iconType={iconType} />
      {label}
    </Label>
    <div className="flex gap-2">
      <Input
        type="url"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 flex-1"
        data-testid={testId}
      />
      {value && (
        <button
          type="button"
          onClick={() => setValue('')}
          className="text-red-400 hover:text-red-300 px-2 shrink-0"
          title="Clear"
          data-testid={clearTestId}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

export default MetaEditorModal;
